import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase client for the platform admin.
 *
 * Supabase is the identity provider for Easel: the API verifies its tokens (ES256, via the
 * project's published JWKS) alongside the legacy AMS token, so this app can sign a person in
 * through Supabase and keep talking to the same API. That is also what makes SSO a configuration
 * change — enabling a provider here needs no work in this file beyond calling it.
 *
 * There is ONE login for the platform. Easel is a feature area of this app, not a separate
 * product with its own sign-in, so this sits beside the existing email/password form rather than
 * replacing it. Both paths end in the same authStore holding a bearer token.
 *
 * Optional by design: with no project configured the app behaves exactly as it did before, and the
 * Supabase affordances hide themselves. A missing env var must degrade to "the old login still
 * works", never to a blank screen.
 */
const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

export const isSupabaseConfigured = Boolean(url && anonKey)

let client: SupabaseClient | null = null

export function getSupabase(): SupabaseClient | null {
  if (!isSupabaseConfigured) return null
  if (!client) {
    client = createClient(url as string, anonKey as string, {
      auth: {
        // The app already persists its bearer token in authStore; letting the Supabase SDK keep
        // its own session in localStorage too is what allows a refresh to survive a reload and a
        // token to be renewed without sending the artist back to the login screen.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  }
  return client
}
